import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { GetCategoryPickerResponse } from '@/api/handlers/resources/categories/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { CategoryQueryKeys } from './categoryQueryKeys';

/**
 * Reads every live category from `GET /categories/picker` (`{ items: [...] }`,
 * each `{ id, name, slug, imageUrl, parentId }`). Drives the seller POS category
 * filter, which reads the picker rather than the paged list: the list's page
 * size stopped the filter at 20 categories, leaving the rest of the catalog
 * unreachable by filtering. Like the other resource queries, the query function
 * returns an `ApiResponse` envelope and never rejects — consumers branch on
 * `data.success`.
 */
export const useCategoryPickerQuery = (enabled = true) => {
    const { apiClient } = useApi();

    return useQuery<ApiResponse<GetCategoryPickerResponse>>({
        queryKey: CategoryQueryKeys.picker(),
        queryFn: () => apiClient.categories.getCategoryPicker(),
        enabled,
        // Categories change rarely, so treat the list as never stale: fetch it
        // once and never refetch on mount / window-focus / reconnect. `gcTime`
        // is also infinite so the cached list survives the filter unmounting.
        staleTime: Infinity,
        gcTime: Infinity,
    });
};
